import { Caption1, Input, makeStyles, tokens } from "@fluentui/react-components";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { PRIVACY_NOTICE } from "./privacy-notice";
import { getSession, safeAppReturn, signIn, useSession, type SignInProvider } from "./session";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXL,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  brand: {
    fontSize: tokens.fontSizeBase200,
    letterSpacing: "0.04em",
    color: tokens.colorNeutralForeground3,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightRegular,
  },
  note: {
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  mark: {
    width: "16px",
    height: "16px",
    display: "grid",
    gridTemplateColumns: "7px 7px",
    gap: "2px",
    flexShrink: 0,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  line: {
    flex: 1,
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
});

function MicrosoftMark() {
  const styles = useStyles();
  const squares = ["#f25022", "#7fba00", "#00a4ef", "#ffb900"];
  return (
    <span className={styles.mark} aria-hidden>
      {squares.map((color) => (
        <span key={color} style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

/** 로그인. 다른 플랜은 보이지 않고, 비밀번호 칸은 없다. */
export function LoginPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const session = useSession();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const back = safeAppReturn(search.get("next"));

  const enter = (provider: SignInProvider, value = "") => {
    const next = signIn(provider, value);
    if (!next) {
      setError("회사 이메일 형식으로 입력해 주세요.");
      return;
    }
    navigate(back, { replace: true });
  };

  return (
    <main className={styles.page}>
      <section className={styles.card} aria-labelledby="login-title">
        <div>
          <div className={styles.brand}>WAVEX</div>
          <h1 id="login-title" className={styles.title}>
            로그인
          </h1>
          <Caption1 className={styles.note}>회사 계정으로 들어옵니다.</Caption1>
        </div>
        {session || getSession() ? (
          <div className={styles.actions}>
            <Caption1 className={styles.note}>{(session ?? getSession())?.email} 로 로그인했습니다.</Caption1>
            <FuiButton appearance="primary" onClick={() => navigate(back)}>
              플랜으로
            </FuiButton>
          </div>
        ) : (
          <>
            <div className={styles.actions}>
              <FuiButton appearance="primary" icon={<MicrosoftMark />} onClick={() => enter("microsoft")}>
                Microsoft 365로 계속
              </FuiButton>
              <FuiButton appearance="outline" onClick={() => enter("google")}>
                Google로 계속
              </FuiButton>
            </div>
            <div className={styles.divider} aria-hidden>
              <span className={styles.line} />
              또는
              <span className={styles.line} />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="work-email">
                회사 이메일
              </label>
              <Input
                id="work-email"
                type="email"
                size="small"
                value={email}
                placeholder="name@company.com"
                onChange={(_, data) => {
                  setEmail(data.value);
                  setError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") enter("email", email);
                }}
              />
              {error ? <Caption1 className={styles.note}>{error}</Caption1> : null}
            </div>
            <FuiButton appearance="outline" onClick={() => enter("email", email)}>
              이메일로 계속
            </FuiButton>
            <Caption1 className={styles.note}>이 브라우저는 30일 동안 로그인을 유지합니다. 비밀번호는 없습니다.</Caption1>
          </>
        )}
        <Caption1 className={styles.note}>{PRIVACY_NOTICE}</Caption1>
      </section>
    </main>
  );
}
